import { capabilities, capabilityHref } from './capabilities';
import { isMarketingRouteActive } from './marketing-navigation';

/** Outcome-led entry points, distinct from the capabilities that power them. */
export const workflows = [
  {
    slug: 'continuous-account-prioritization',
    title: 'Continuous account prioritization',
    navigationDescription:
      'Know which accounts need attention this week, and why.',
    audience: 'For sales and revenue teams',
    headline: 'A current priority list. A reason for every account.',
    description:
      'Lumnis monitors your account tiers, combines buying-group activity and company changes with CRM context, and delivers a weekly priority list with evidence and next steps.',
    diagram: 'account',
    setup: [
      'Your account list and Tier 1, Tier 2, and Tier 3 criteria',
      'Account owners and the review cadence for each tier',
      'The signals that matter to your sales motion',
      'CRM context, including opportunities and previous conversations'
    ],
    steps: [
      {
        title: 'Keep your account universe covered',
        description:
          'Start with your account tiers. Lumnis reviews named and SDR-managed accounts weekly, with programmatic coverage for the broader account universe. Account fit sets the tier; new evidence changes the priority.'
      },
      {
        title: 'Stack signals against the account history',
        description:
          'Lumnis connects buying-group posts and engagement, leadership changes, hiring, and company news with your CRM. A single event is context. Several relevant changes together can give your team a reason to act.'
      },
      {
        title: 'Surface this week’s priorities',
        description:
          'Lumnis separates accounts with meaningful changes from those that should stay in monitoring. Your team receives a ranked review list, not a new alert for every event.'
      },
      {
        title: 'Give the owner an evidence-backed next step',
        description:
          'Each priority includes what changed, why it matters, dated evidence, a suggested next move, and an assigned owner. Your rep can inspect the reasoning and decide how to act.'
      }
    ],
    repeatTitle: 'The next review starts with context, not from scratch.',
    repeatDescription:
      'Accounts that do not warrant action remain covered. Lumnis adds new signals to the existing account picture, so your team can see when the reason to engage becomes stronger or changes.',
    outputs: [
      {
        title: 'A weekly priority list',
        description:
          'Accounts worth reviewing now, organized around your tiers and owners.'
      },
      {
        title: 'A brief for each priority',
        description:
          'The reason, dated evidence, CRM context, and recommended next step.'
      },
      {
        title: 'Continued coverage',
        description:
          'The rest of your account list stays monitored for relevant changes.'
      }
    ],
    questions: [
      {
        question: 'Does Lumnis replace our account tiers?',
        answer:
          'No. You set the tiers and criteria. Lumnis uses fresh evidence to help prioritize attention within your account strategy.'
      },
      {
        question: 'What makes this different from a list of signals?',
        answer:
          'The output connects multiple signals to an account and its CRM history. Your rep sees why the combination matters and what to do next, with the underlying evidence attached.'
      },
      {
        question: 'Who takes action?',
        answer:
          'Your assigned account owner receives the brief and decides how to act. The prioritization workflow gives them the context for that decision.'
      }
    ]
  },
  {
    slug: 'compounding-audience-engine',
    title: 'Compounding audience engine',
    navigationDescription:
      'Grow your ICP audience, publish relevant content, and follow up.',
    audience: 'For marketing and sales teams',
    headline: 'The right audience. Relevant content. A reason to follow up.',
    description:
      'You define the ICP. Lumnis finds relevant prospects, grows your audience, researches buyer priorities, publishes content, and follows up with people who engage. Each cycle informs the next.',
    diagram: 'audience',
    setup: [
      'Your ICP and the people you want to reach',
      'The LinkedIn profiles participating in audience growth',
      'Your positioning and the topics relevant to your business',
      'A daily or weekly publishing cadence'
    ],
    steps: [
      {
        title: 'Find the right prospects',
        description:
          'Lumnis finds people at ICP-fit accounts and builds a fresh prospect list each week. Prospecting keeps the engine focused on the buyers you want to reach.'
      },
      {
        title: 'Grow your audience',
        description:
          'Lumnis sends weekly LinkedIn connection requests to those prospects from participating profiles. Your audience grows around the people on your prospect list.'
      },
      {
        title: 'Research buyer priorities',
        description:
          'Lumnis studies buyers’ questions, priorities, and conversations to identify topics worth posting about. Your team does not need to start each publishing cycle with a blank content calendar.'
      },
      {
        title: 'Publish relevant content',
        description:
          'Lumnis creates relevant posts from the research and publishes on your daily or weekly cadence. The topics come from the audience, not a disconnected list of content ideas.'
      },
      {
        title: 'Follow up on engagement',
        description:
          'Lumnis identifies who engages and reaches out with context from the content and the person. Engagement gives the conversation a starting point; it is not treated as proof that someone is ready to buy.'
      }
    ],
    repeatTitle: 'Audience, content, and outreach improve together.',
    repeatDescription:
      'New connections expand who you can reach. Audience research informs what you publish. Lumnis uses engagement and replies to refine the next prospect list, content, and follow-ups. Each cycle starts with what the previous one taught you.',
    outputs: [
      {
        title: 'An audience of relevant buyers',
        description: 'Weekly connections with people who match your ICP.'
      },
      {
        title: 'Buyer-informed content',
        description:
          'Posts grounded in buyer priorities, published on your cadence.'
      },
      {
        title: 'Pipeline from engaged buyers',
        description:
          'Relevant follow-ups help turn audience engagement into sales conversations and pipeline, with replies informing the next cycle.'
      }
    ],
    questions: [
      {
        question: 'Do we have to choose every topic ourselves?',
        answer:
          'No. Lumnis researches your audience to determine which questions and priorities are worth covering. Your ICP and positioning provide the direction.'
      },
      {
        question: 'How often does the engine run?',
        answer:
          'Audience building includes weekly connection requests. Content can publish daily or weekly, with engagement and replies feeding the ongoing research and follow-up cycle.'
      },
      {
        question: 'How does it connect marketing and sales?',
        answer:
          'Marketing content reaches the intended audience, and Lumnis connects the people who engage to contextual follow-ups. Audience growth, publishing, and outreach share the same research context.'
      }
    ]
  }
] as const;

export type Workflow = (typeof workflows)[number];

export function workflowHref(workflow: Workflow): string {
  return `/${workflow.slug}`;
}

export function isProductNavigationActive(pathname: string): boolean {
  return (
    isMarketingRouteActive(pathname, '/product') ||
    workflows.some((workflow) =>
      isMarketingRouteActive(pathname, workflowHref(workflow))
    ) ||
    capabilities.some((capability) => pathname === capabilityHref(capability))
  );
}
